/* eslint-disable react/prop-types */
import React from 'react'
import {
    Grid,
    Text,
    Box,
    AspectRatio,
    Button,
    Flex,
    useMediaQuery,
    useStyleConfig,
    ModalCloseButton,
} from '@chakra-ui/react'

import {useCompare} from '../../context/compare'
import {CloseIcon} from '../../components/icons'
import {breakpoints} from '../../theme/_utils'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {productUrlBuilder} from '../../utils/url'

import loadable from '@loadable/component'
const ReviewScoreIcon = loadable(() => import('../../components/review/ReviewScoreIcon'))

import EnergyLabel from '../../components/energy-label'
import DisplayPrice from '../../components/display-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import Link from '@salesforce/retail-react-app/app/components/link'
import {FormattedMessage, useIntl} from 'react-intl'
import useProductData from '../../hooks/use-product-data'
import OmnibusPrice from '../../components/display-price/omnibus-price'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {getImageForProductTile} from '../../utils/utils'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'

const CompareModalTile = ({product}) => {
    const {removeItem} = useCompare()
    const purl = productUrlBuilder(product)
    const {priceData} = useProductData(product)
    const {currency} = useCurrency()
    const intl = useIntl()
    const {addLayer} = useGoogleTagManager()
    const [isMobile] = useMediaQuery(`(max-width: ${breakpoints.lg})`)
    const styles = useStyleConfig('ProductComparePlp')
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const imgSrc = getImageForProductTile(product)

    return (
        <Grid {...styles.tileCard}>
            <Flex {...styles.closeGroup}>
                {!isMobile && (
                    <Text {...styles.closeMessage}>
                        <FormattedMessage
                            id="product_list.compare_button.clear"
                            defaultMessage="Remove"
                        />
                    </Text>
                )}
                <CloseIcon onClick={() => removeItem(product)} boxSize="iconsSize.small" />
            </Flex>
            <AspectRatio {...styles.tileImage}>
                <DynamicImage
                    src={imgSrc}
                    imageProps={{
                        // treat img as a decorative item, we don't need to pass `image.alt`
                        // since it is the same as product name
                        // which can cause confusion for individuals who uses screen readers
                        alt: '',
                        loading: 'lazy',
                    }}
                />
            </AspectRatio>
            <Grid {...styles.tileBody}>
                <Text {...styles.tileProductName}>
                    {product?.representedProduct?.c_variantIdentifier ||
                        product?.name ||
                        product?.productName}
                </Text>
                <Text {...styles.tileProductId}>{product.productId}</Text>
                <Box {...styles.tilePrice}>
                    <DisplayPrice
                        priceData={priceData}
                        currency={currency}
                        listPriceProps={styles.listPriceProps}
                        currentPriceProps={styles.currentPriceProps}
                        notShoppableCountry={notShoppableCountry}
                    />
                    {!notShoppableCountry && priceData.showOmnibusPriceDiscount && (
                        <OmnibusPrice
                            priceData={priceData}
                            currency={currency}
                            omnibusExpandedView
                            variant="recommended"
                        />
                    )}
                </Box>

                {product?.representedProduct?.c_energyEfficiencyClass && (
                    <EnergyLabel
                        level={product?.representedProduct?.c_energyEfficiencyClass}
                        description={intl.formatMessage({
                            defaultMessage: 'Product information',
                            id: 'energy_label.product_information',
                        })}
                        textWidth={isMobile ? 'large' : 'medium'}
                        labelPosition={isMobile ? 'left' : 'top'}
                        extraStyleDescription={styles.energyLabelStyle}
                        manualDocument={product?.representedProduct?.c_manualDocument}
                    />
                )}
                <Box {...styles.reviewsContainer}>
                    <ReviewScoreIcon id={product.productId} />
                </Box>
            </Grid>
            <Link
                to={purl}
                onClick={async () =>
                    await addLayer(
                        basic_gtm_event.bind(this, {
                            event: 'comparisonTool',
                            eventCategory: 'ecommerce',
                            eventAction: 'comparisonToolListing',
                            eventLabel: `linkToPdp - ${purl}`,
                        }),
                    )
                }
            >
                <Button {...styles.tileDiscoverButton}>
                    <FormattedMessage
                        id="product_list.tile.button.discover_more"
                        defaultMessage="Discover more"
                    />
                </Button>
            </Link>
        </Grid>
    )
}

const CompareModalTiles = () => {
    const {
        state: {compareItems},
    } = useCompare()
    const styles = useStyleConfig('ProductComparePlp')

    return (
        <Grid {...styles.modalTiles}>
            <Flex {...styles.modalTilesHeader}>
                <Text {...styles.modalTilesLabel}>
                    <FormattedMessage
                        id="product_list.compare_products.label"
                        defaultMessage="Compare products: {items}/3 "
                        values={{
                            items: compareItems.length,
                        }}
                    />
                </Text>
                <ModalCloseButton {...styles.modalClose}>
                    <CloseIcon {...styles.modalCloseIcon} />
                </ModalCloseButton>
            </Flex>
            <Grid {...styles.tileGrid} templateColumns={`repeat(${compareItems.length}, 1fr)`}>
                {compareItems?.map((product, i) => (
                    <CompareModalTile key={product.productId} product={product} productIndex={i} />
                ))}
            </Grid>
        </Grid>
    )
}

export default CompareModalTiles
